import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LeadRole, IdealCustomerProfile } from "@sales-ops-amplify/shared";
import { createIcp, listIcps, updateIcp } from "@/api/icp";
import { cn } from "@/cn";

const PERSONAS: LeadRole[] = [
  "buyer",
  "champion",
  "influencer",
  "procurement",
  "referrer",
  "other",
];

type IcpForm = {
  name: string;
  targetSegments: string;
  targetDealSizeMin: string;
  targetDealSizeMax: string;
  icpKeywords: string;
  industries: string;
  geographies: string;
  requiredPersonas: LeadRole[];
  notes: string;
};

const EMPTY: IcpForm = {
  name: "",
  targetSegments: "",
  targetDealSizeMin: "",
  targetDealSizeMax: "",
  icpKeywords: "",
  industries: "",
  geographies: "",
  requiredPersonas: [],
  notes: "",
};

function toForm(icp: IdealCustomerProfile): IcpForm {
  return {
    name: icp.name ?? "",
    targetSegments: (icp.targetSegments ?? []).join(", "),
    targetDealSizeMin: icp.targetDealSizeMin != null ? String(icp.targetDealSizeMin) : "",
    targetDealSizeMax: icp.targetDealSizeMax != null ? String(icp.targetDealSizeMax) : "",
    icpKeywords: (icp.icpKeywords ?? []).join(", "),
    industries: (icp.industries ?? []).join(", "),
    geographies: (icp.geographies ?? []).join(", "),
    requiredPersonas: icp.requiredPersonas ?? [],
    notes: icp.notes ?? "",
  };
}

function parseList(value: string): string[] {
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}

export function SettingsIcpPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["icp"],
    queryFn: listIcps,
    staleTime: 30_000,
  });

  const [form, setForm] = useState<IcpForm>(EMPTY);
  const [activeId, setActiveId] = useState<string | undefined>();
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!data || initialized) return;
    const active =
      data.icps.find((i) => i.id === data.activeIcpId) ?? data.icps[0];
    if (active) {
      setForm(toForm(active));
      setActiveId(active.id);
    }
    setInitialized(true);
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim() || "Default ICP",
        targetSegments: parseList(form.targetSegments),
        targetDealSizeMin: form.targetDealSizeMin
          ? Number(form.targetDealSizeMin)
          : undefined,
        targetDealSizeMax: form.targetDealSizeMax
          ? Number(form.targetDealSizeMax)
          : undefined,
        icpKeywords: parseList(form.icpKeywords),
        industries: parseList(form.industries),
        geographies: parseList(form.geographies),
        requiredPersonas: form.requiredPersonas,
        notes: form.notes.trim() || undefined,
        setActive: true as const,
      };
      if (activeId) {
        return updateIcp(activeId, payload);
      }
      return createIcp(payload);
    },
    onSuccess: (icp) => {
      setActiveId(icp.id);
      setForm(toForm(icp));
      qc.invalidateQueries({ queryKey: ["icp"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    },
    onError: () => {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    },
  });

  function togglePersona(role: LeadRole) {
    setForm((prev) => {
      const has = prev.requiredPersonas.includes(role);
      return {
        ...prev,
        requiredPersonas: has
          ? prev.requiredPersonas.filter((r) => r !== role)
          : [...prev.requiredPersonas, role],
      };
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Failed to load ICP. Please try again later.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <header className="mb-2 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Personas</h2>
          <p className="text-sm text-muted-foreground">
            Define ideal customer personas and ICP segments used in People search and enrichment.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSaveStatus("saving");
            saveMutation.mutate();
          }}
          disabled={saveMutation.isPending || !form.name.trim()}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition",
            saveStatus === "saved"
              ? "bg-success/20 text-success"
              : saveStatus === "error"
                ? "bg-destructive/20 text-destructive"
                : "bg-brand text-brand-foreground hover:opacity-90",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {saveStatus === "saved"
            ? "Saved!"
            : saveStatus === "error"
              ? "Error"
              : saveMutation.isPending
                ? "Saving…"
                : activeId
                  ? "Save ICP"
                  : "Create ICP"}
        </button>
      </header>

      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <Field label="Name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            placeholder="e.g. Mid-market SaaS ICP"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Min. Deal Size">
            <input
              type="number"
              value={form.targetDealSizeMin}
              onChange={(e) =>
                setForm((f) => ({ ...f, targetDealSizeMin: e.target.value }))
              }
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
              placeholder="25000"
            />
          </Field>
          <Field label="Max. Deal Size">
            <input
              type="number"
              value={form.targetDealSizeMax}
              onChange={(e) =>
                setForm((f) => ({ ...f, targetDealSizeMax: e.target.value }))
              }
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
              placeholder="250000"
            />
          </Field>
        </div>

        <Field label="Target Segments (comma-separated)">
          <input
            type="text"
            value={form.targetSegments}
            onChange={(e) =>
              setForm((f) => ({ ...f, targetSegments: e.target.value }))
            }
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            placeholder="SaaS, FinTech, Healthcare IT"
          />
        </Field>

        <Field label="ICP Keywords (comma-separated)">
          <input
            type="text"
            value={form.icpKeywords}
            onChange={(e) =>
              setForm((f) => ({ ...f, icpKeywords: e.target.value }))
            }
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            placeholder="series-b, product-led, enterprise-ready"
          />
        </Field>

        <Field label="Industries (comma-separated)">
          <input
            type="text"
            value={form.industries}
            onChange={(e) =>
              setForm((f) => ({ ...f, industries: e.target.value }))
            }
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            placeholder="Software, Financial Services"
          />
        </Field>

        <Field label="Geographies (comma-separated)">
          <input
            type="text"
            value={form.geographies}
            onChange={(e) =>
              setForm((f) => ({ ...f, geographies: e.target.value }))
            }
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            placeholder="US, UK, DACH"
          />
        </Field>

        <Field label="Required Personas">
          <div className="flex flex-wrap gap-2">
            {PERSONAS.map((role) => {
              const active = form.requiredPersonas.includes(role);
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => togglePersona(role)}
                  className={cn(
                    "rounded-md border px-3 py-1.5 text-xs font-medium capitalize transition",
                    active
                      ? "border-brand bg-brand/10 text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {role}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            placeholder="Qualifiers, disqualifiers, geo notes…"
          />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}
